
import { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import {
  collection,
  getFirestore,
} from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";
import { useNavigate } from "react-router-dom";

import { firebaseApp } from "../config/firebase";
import { Loader } from "../hooks/useLoading";

const db = getFirestore(firebaseApp);

const EmailTemplate = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  /*
   * Get email templates from Firebase
   */
  const [snapshot, loading, error] = useCollection(
    collection(db, "email_templates"),
    {
      snapshotListenOptions: {
        includeMetadataChanges: true,
      },
    },
  );

  /*
   * Convert Firestore timestamp to milliseconds
   */
  const toMillis = (value) => {
    if (!value) return 0;

    if (typeof value === "object" && typeof value.seconds === "number") {
      return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value === "number") {
      return value;
    }

    const timestamp = Date.parse(String(value));

    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  /*
   * Convert Firestore documents into array and sort
   */
  const templates = useMemo(() => {
    if (!snapshot?.docs) {
      return [];
    }

    return snapshot.docs
      .map((item) => ({
        id: item.id,
        ...item.data(),
      }))
      .sort((a, b) => toMillis(b.updated_at) - toMillis(a.updated_at));
  }, [snapshot]);

  const formatTemplateName = (name) => {
    if (!name) return "";
    const formatted = name.replace(/_/g, " ");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  /*
   * Search
   */
  const filteredTemplates = useMemo(() => {
    const searchValue = String(search || "")
      .trim()
      .toLowerCase();

    if (!searchValue) {
      return templates;
    }

    return templates.filter((item) =>
      formatTemplateName(item.id || "")
        .toLowerCase()
        .includes(searchValue) ||
      String(item.subject || "")
        .toLowerCase()
        .includes(searchValue)
    );
  }, [templates, search]);

  /*
   * Pagination
   */
  const totalPages = Math.max(
    1,
    Math.ceil(filteredTemplates.length / rowsPerPage),
  );

  const paginatedTemplates = filteredTemplates.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search, rowsPerPage]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate();
    return `${date.toLocaleDateString("en-GB")} ${date.toLocaleTimeString()}`;
  };

  if (error) {
    console.error("Email templates Firestore error:", error);
  }

  return (
    <>
      {loading && <Loader />}

      {/* Header */}
      <header className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100">
        <div>
          <h4 className="mb-1">Email Templates</h4>
          <small className="text-muted">
            Manage email templates for the application.
          </small>
        </div>
      </header>

      {/* Search */}
      <div className="d-flex justify-content-between align-items-end gap-3 mb-3">
        <div className="d-flex flex-column">
          <label className="mb-1">Search Templates:</label>
          <input
            type="text"
            className="form-control"
            style={{ minWidth: "300px" }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or subject"
          />
        </div>
        <span className="badge text-bg-secondary px-3 py-2">
          Total: {filteredTemplates.length}
        </span>
      </div>

      {/* Table */}
      {paginatedTemplates.length > 0 ? (
        <div className="table-responsive">
          <table className="table">
            <thead className="table-secondary">
              <tr>
                <th style={{ width: "70px" }}>#</th>
                <th>Name</th>
                <th>Subject</th>
                <th style={{ width: "200px" }}>Updated On</th>
                <th style={{ width: "120px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTemplates.map((template, index) => (
                <tr key={template.id}>
                  <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                  <td>
                    <strong>{formatTemplateName(template.id)}</strong>
                  </td>
                  <td
                    className="text-truncate"
                    style={{ maxWidth: "300px" }}
                    title={template.subject}
                  >
                    {template.subject}
                  </td>
                  <td>{formatTimestamp(template.updated_at)}</td>
                  <td className="text-center">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/dashboard/email-templates/${template.id}`)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && (
          <div className="text-center py-5 border rounded">
            <h5>No Email Templates Found</h5>

            {/* <p className="text-muted mb-3">
            Add a connection reason to make it available in the mobile app.
            </p> */}
        </div>
        )
      )}

      {/* Pagination */}
      {!loading && filteredTemplates.length > 0 && (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3 p-3 border rounded bg-light">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge text-bg-secondary px-3 py-2">
              Total: {filteredTemplates.length}
            </span>
            <label htmlFor="templates-per-page" className="mb-0 fw-semibold">
              Per page:
            </label>
            <select
              id="templates-per-page"
              className="form-control form-control-sm"
              style={{ width: "90px" }}
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="text-muted small">
              Showing{" "}
              {filteredTemplates.length === 0
                ? 0
                : (currentPage - 1) * rowsPerPage + 1}{" "}
              - {Math.min(currentPage * rowsPerPage, filteredTemplates.length)} of{" "}
              {filteredTemplates.length}
            </span>
            <div className="btn-group" role="group">
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              <button className="btn btn-primary btn-sm" disabled>
                Page {currentPage} / {totalPages}
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailTemplate;